import React from 'react';
import {
  Button,
  CircularProgress,
  Collapse,
  Typography,
} from '@material-ui/core';
import moment from 'moment';
import { SearchActivityResponse } from '../../models';

const timeDisplayFormat = 'YYYY-MM-DD HH:mm';

type SearchActivityPanelProps = {
  unknownCode: string;
  isExpanded: boolean;
  isLoading: boolean;
  data: SearchActivityResponse | null;
};

export const SearchActivityPanel: React.FC<SearchActivityPanelProps> = ({
  unknownCode,
  isExpanded,
  isLoading,
  data,
}) => (
  <Collapse in={isExpanded}>
    <div
      style={{
        marginTop: 8,
        padding: 8,
        backgroundColor: '#f5f5f5',
        borderRadius: 4,
        minWidth: 320,
      }}
    >
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
          <CircularProgress size={20} />
        </div>
      )}
      {!isLoading && data && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            POS search activity — {unknownCode}
          </Typography>
          {data.summary.search_terms.length === 0 &&
          data.summary.product_numbers.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No POS search/add tracking recorded for this code.
            </Typography>
          ) : (
            <>
              <Typography variant="body2">
                <strong>Search terms:</strong>{' '}
                {data.summary.search_terms.length > 0
                  ? data.summary.search_terms.join(', ')
                  : '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Products added:</strong>{' '}
                {data.summary.product_numbers.length > 0
                  ? data.summary.product_numbers.join(', ')
                  : '—'}
              </Typography>
              <Typography variant="body2" style={{ marginBottom: 8 }}>
                <strong>Product names:</strong>{' '}
                {data.summary.product_names.length > 0
                  ? data.summary.product_names.join(', ')
                  : '—'}
              </Typography>
            </>
          )}
          {data.entries.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th align="left">Time</th>
                    <th align="left">Receipt</th>
                    <th align="left">Scan idx</th>
                    <th align="left">Search</th>
                    <th align="left">Added</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((entry, index) => (
                    <tr key={`${entry.history_id}-${entry.searched_at || index}`}>
                      <td>
                        {entry.created_time
                          ? moment(entry.created_time).format(timeDisplayFormat)
                          : '—'}
                      </td>
                      <td>{entry.receipt_number || '—'}</td>
                      <td>{entry.scan_index}</td>
                      <td>{entry.search_term || '—'}</td>
                      <td>
                        {entry.product_number
                          ? `${entry.product_number}${entry.product_name ? ` — ${entry.product_name}` : ''}`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  </Collapse>
);

type SearchActivityToggleProps = {
  isExpanded: boolean;
  onToggle: (event: React.MouseEvent) => void;
};

export const SearchActivityToggle: React.FC<SearchActivityToggleProps> = ({
  isExpanded,
  onToggle,
}) => (
  <Button size="small" color="primary" variant="outlined" onClick={onToggle}>
    Search {isExpanded ? '▲' : '▼'}
  </Button>
);

export default SearchActivityPanel;
